import { useEffect, useState } from "react";
import { Trash2, MailOpen, FileDown, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Pager from "@/components/site/Pager";
import EnquiryDetailDialog from "@/components/admin/EnquiryDetailDialog";
import { api, API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";

const PAGE_SIZE = 25;

export default function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [type, setType] = useState("");
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/admin/enquiries", { params: { type: type || undefined, page, page_size: PAGE_SIZE } })
    .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, page]);
  useEffect(() => { setPage(1); }, [type]);

  const toggleRead = async (id, val) => { await api.patch(`/admin/enquiries/${id}/read?read=${val}`); load(); };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/enquiries/${id}`); toast.success("Deleted"); load(); setDetail(null); };

  const openDetail = async (row) => {
    setDetail(row);
    if (!row.is_read) {
      await api.patch(`/admin/enquiries/${row.id}/read?read=true`);
      load();
    }
  };

  const exportCsv = async () => {
    const token = getToken();
    const params = new URLSearchParams();
    if (type) params.set("type", type);
    const res = await fetch(`${API_BASE}/admin/exports/enquiries?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Export failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `enquiries-${new Date().toISOString().slice(0,10)}.csv`; link.click();
    URL.revokeObjectURL(url);
    toast.success("Exported");
  };

  return (
    <div className="space-y-6" data-testid="admin-enquiries">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Enquiries</h1>
          <p className="text-sm text-slate-500">Contact-us and callback requests. For hire-talent briefs use the dedicated Hire Briefs tab.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
            <SelectTrigger className="md:w-48" data-testid="enquiries-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="contact">Contact</SelectItem>
              <SelectItem value="hire_talent">Hire Talent</SelectItem>
              <SelectItem value="callback">Callback</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportCsv} data-testid="enquiries-export-csv"><FileDown className="mr-1 h-4 w-4" />CSV</Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <ul className="divide-y">
          {items.map((e) => (
            <li key={e.id} className="p-4 hover:bg-slate-50">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-slate-900">{e.name}</span>
                    <Badge className={
                      e.type === "hire_talent" ? "bg-secondary/15 text-secondary" :
                      e.type === "callback" ? "bg-blue-100 text-blue-700" :
                      "bg-slate-100 text-slate-700"
                    }>{e.type}</Badge>
                    {!e.is_read && <Badge className="bg-blue-100 text-blue-700">New</Badge>}
                  </div>
                  <div className="text-xs text-slate-500">{e.email || "—"} · {e.phone || "—"} · {new Date(e.created_at).toLocaleString()}</div>
                  {e.company && <div className="text-xs text-slate-500">Company: <b>{e.company}</b></div>}
                  {e.subject && <div className="mt-1 text-sm font-medium text-slate-800">{e.subject}</div>}
                  <p className="mt-1 line-clamp-2 text-sm text-slate-700">{e.message}</p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button variant="outline" size="sm" onClick={() => openDetail(e)} data-testid={`enquiry-view-${e.id}`}>
                    <Eye className="mr-1 h-3.5 w-3.5" /> View
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleRead(e.id, !e.is_read)} data-testid={`enquiry-read-${e.id}`} title={e.is_read ? "Mark unread" : "Mark read"}><MailOpen className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(e.id)} data-testid={`enquiry-delete-${e.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                </div>
              </div>
            </li>
          ))}
          {items.length === 0 && <li className="p-10 text-center text-slate-400">No enquiries yet.</li>}
        </ul>
      </div>
      <Pager page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} testIdPrefix="admin-enquiries-pager" />

      <EnquiryDetailDialog
        enquiry={detail}
        open={!!detail}
        onClose={() => setDetail(null)}
        onToggleRead={(row) => { toggleRead(row.id, !row.is_read); setDetail({ ...row, is_read: !row.is_read }); }}
        onDelete={(row) => remove(row.id)}
      />
    </div>
  );
}
